import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon, AlertCircle, Plus, Crop } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { createArchivePost, type ArchivePostError } from '@/store/slices/archiveSlice';
import { friendlyMessageForCode } from '@/lib/errorMessages';
import { ERR } from '@/lib/apiErrors';
import { toast } from 'react-hot-toast';
import { trackArchiveCreated } from '@/lib/posthog';
import ModernImageCropper from '../portfolio/ModernImageCropper';
import MobileImageCapture from '../portfolio/MobileImageCapture';
import { smartCompress } from '@/lib/imageCompression';
import MobileModal from '@/components/shared/MobileModal';
import MentionTextarea from '@/components/shared/MentionTextarea';
import PostFiltersSection from './PostFiltersSection';
import { EMPTY_FILTERS, type PostFilters } from '@/config/filters';
import {
  ACCEPT_ATTRIBUTE,
  ACCEPTED_FORMATS_LABEL,
  isAcceptedImage,
  isBrowserReadableImage,
} from '@/config/uploadFormats';

interface ArchiveUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
}

/** Shown only when nothing more specific could be determined. */
const UPLOAD_FALLBACK_MESSAGE = "We couldn't create your post. Please try again.";

/**
 * Give up when the connection has moved no bytes at all for this long. A slow
 * upload that is still making progress is never cut off — only a dead one.
 */
const STALL_TIMEOUT_MS = 60_000;
const STALL_CHECK_MS = 5_000;

/**
 * Once the last byte is sent there are no more progress events, so the stall
 * watchdog can't tell "server is working" from "connection died". Give the
 * server its own, more generous deadline to store the images and reply.
 */
const PROCESSING_TIMEOUT_MS = 120_000;

/**
 * All photos go up in a single multipart request, so progress is only
 * knowable in aggregate — per-file bars would have to be invented.
 * 'processing' covers the gap after the last byte lands while the server
 * stores the images and writes the post.
 */
type UploadPhase = 'idle' | 'uploading' | 'processing';

const formatMB = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)}MB`;

/** Unique id for one post attempt. randomUUID is unavailable on older Safari. */
const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const ArchiveUploadModal: React.FC<ArchiveUploadModalProps> = ({ isOpen, onClose, onUpload }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle');
  const [bytesSent, setBytesSent] = useState(0);
  const [bytesTotal, setBytesTotal] = useState(0);
  const [caption, setCaption] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [filters, setFilters] = useState<PostFilters>(EMPTY_FILTERS);
  const [status, setStatus] = useState<'draft' | 'published'>('published');
  const [coverIndex, setCoverIndex] = useState(0);
  const [cropIndex, setCropIndex] = useState<number | null>(null);
  const [showMobileCapture, setShowMobileCapture] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // In-flight upload bookkeeping. Refs, not state: the stall watchdog and the
  // Cancel handler must read the latest values without re-rendering.
  const abortRef = useRef<AbortController | null>(null);
  /** Why the request was aborted, so we can tell "user cancelled" from "dead connection". */
  const abortReasonRef = useRef<'user' | 'stalled' | null>(null);
  const lastProgressAtRef = useRef<number>(0);
  const stallTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ref, not `uploadPhase`: the progress callback closes over a stale render. */
  const processingStartedRef = useRef(false);
  /**
   * Identifies this post across retries. Held for the whole compose session
   * so tapping Create again after a failure can never produce a duplicate,
   * and rotated once a post actually lands so the next one is its own post.
   */
  const idempotencyKeyRef = useRef<string>(newIdempotencyKey());

  const clearUploadTimers = () => {
    if (stallTimerRef.current !== null) {
      clearInterval(stallTimerRef.current);
      stallTimerRef.current = null;
    }
    if (processingTimerRef.current !== null) {
      clearTimeout(processingTimerRef.current);
      processingTimerRef.current = null;
    }
  };

  // Never leave a request running behind a closed modal.
  useEffect(() => {
    return () => {
      clearUploadTimers();
      abortRef.current?.abort();
    };
  }, []);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  const MAX_FILES = 10;
  const MAX_CAPTION_LENGTH = 2200;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = async (files: File[]) => {
    setError(null);

    // Check total file count
    if (selectedFiles.length + files.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} images allowed per post`);
      return;
    }

    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    for (const file of files) {
      // Accepts by extension as well as mimetype — iPhone photos often arrive
      // with no usable type, and rejecting those is the single most common
      // way an upload fails before it even starts.
      if (!isAcceptedImage(file)) {
        setError(`"${file.name}" isn't an image we can use. Accepted formats: ${ACCEPTED_FORMATS_LABEL}.`);
        continue;
      }

      try {
        // Only compress what this browser can actually decode. HEIC would
        // fail both compression passes and waste seconds per photo on a
        // phone; the server converts it to JPEG instead.
        const processedFile = isBrowserReadableImage(file)
          ? await smartCompress(file, {
              maxSizeMB: 1.5, // Target 1.5MB max (most images will be in KB range)
              targetReduction: 0.3, // 70% reduction (10MB → 3MB)
              maxDimension: 2048,
              quality: 0.70
            })
          : file;

        // Final size check after compression
        if (processedFile.size > MAX_FILE_SIZE) {
          setError(`"${file.name}" is too big (${(processedFile.size / 1024 / 1024).toFixed(1)}MB). Photos must be under 10MB — please pick a smaller one.`);
          continue;
        }

        validFiles.push(processedFile);

        // Object URL, not a base64 data URL. Ten photos as data URLs is
        // ~20MB of strings held in React state and re-rendered on every
        // keystroke in the caption — enough to kill the tab on an older
        // phone. An object URL is just a handle to the existing blob.
        newPreviews.push(URL.createObjectURL(processedFile));

      } catch (err) {
        console.error('Error processing file:', err);
        setError(`Failed to process "${file.name}". Please try again.`);
      }
    }

    // Update both arrays together to keep them in sync
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setPreviewUrls(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
    // Keep the cover pointing at a photo that still exists.
    setCoverIndex(prev => (index === prev ? 0 : index < prev ? prev - 1 : prev));
  };

  const handleCropComplete = (index: number, croppedBlob: Blob) => {
    // Convert blob to file
    const fileName = selectedFiles[index].name;
    const croppedFile = new File([croppedBlob], fileName, { type: 'image/jpeg' });
    
    // Update file and preview
    setSelectedFiles(prev => prev.map((file, i) => i === index ? croppedFile : file));
    
    // Swap in a preview for the cropped blob and release the previous one.
    setPreviewUrls(prev =>
      prev.map((url, i) => {
        if (i !== index) return url;
        URL.revokeObjectURL(url);
        return URL.createObjectURL(croppedFile);
      })
    );
    
    setCropIndex(null);
    toast.success('Image adjusted successfully!');
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    if (caption.length > MAX_CAPTION_LENGTH) {
      setError(`Caption must be ${MAX_CAPTION_LENGTH} characters or less`);
      return;
    }

    // A fresh attempt starts clean — a banner from the last try reads as
    // "still broken" while the user is already retrying.
    setError(null);
    setIsUploading(true);
    setUploadPhase('uploading');
    setBytesSent(0);
    setBytesTotal(selectedFiles.reduce((sum, file) => sum + file.size, 0));

    const controller = new AbortController();
    abortRef.current = controller;
    abortReasonRef.current = null;
    processingStartedRef.current = false;
    lastProgressAtRef.current = Date.now();

    // Watchdog: a slow upload is fine, a frozen one is not.
    clearUploadTimers();
    stallTimerRef.current = setInterval(() => {
      if (Date.now() - lastProgressAtRef.current < STALL_TIMEOUT_MS) return;
      abortReasonRef.current = 'stalled';
      controller.abort();
    }, STALL_CHECK_MS);

    try {
      // The server positions images in the order they arrive, so the chosen
      // cover has to lead.
      const orderedFiles = [
        selectedFiles[coverIndex],
        ...selectedFiles.filter((_, i) => i !== coverIndex),
      ];
      const result = await dispatch(createArchivePost({
        images: orderedFiles,
        metadata: {
          caption: caption || undefined,
          // Every discovery answer is optional and sent as its own field.
          tags: filters.tags.length > 0 ? filters.tags : undefined,
          occasion: filters.occasions.length > 0 ? filters.occasions : undefined,
          readyIn: filters.readyIn ?? undefined,
          status,
          mentionedUsers: mentionedUsers.length > 0 ? mentionedUsers : undefined,
          idempotencyKey: idempotencyKeyRef.current,
        },
        signal: controller.signal,
        onUploadProgress: (loaded, total) => {
          lastProgressAtRef.current = Date.now();
          setBytesSent(loaded);
          if (total > 0) setBytesTotal(total);
          // Bytes are up; the server is still storing images and saving rows.
          // Progress events stop here, so swap the byte watchdog for a plain
          // deadline — otherwise we'd abort a request that is merely slow to
          // answer, and the post would be created without us knowing.
          if (total > 0 && loaded >= total && !processingStartedRef.current) {
            processingStartedRef.current = true;
            setUploadPhase('processing');
            clearUploadTimers();
            processingTimerRef.current = setTimeout(() => {
              abortReasonRef.current = 'stalled';
              controller.abort();
            }, PROCESSING_TIMEOUT_MS);
          }
        },
      }));

      if (createArchivePost.fulfilled.match(result)) {
        
        // Track archive creation
        trackArchiveCreated({
          archiveId: result.payload.id,
          itemType: 'archive_post',
          hasImage: true,
          properties: {
            image_count: selectedFiles.length,
            has_caption: !!caption,
            tag_count: filters.tags.length,
            status,
            user_id: user?.id,
          },
        });

        toast.success(`Archive post created successfully with ${selectedFiles.length} image${selectedFiles.length > 1 ? 's' : ''}!`);
        
        // Reset form state
        setSelectedFiles([]);
        previewUrls.forEach((url) => URL.revokeObjectURL(url));
        setPreviewUrls([]);
        setCaption('');
        setMentionedUsers([]);
        setFilters(EMPTY_FILTERS);
        setCoverIndex(0);
        setStatus('published');
        // This post is saved; the next one must not reuse its key.
        idempotencyKeyRef.current = newIdempotencyKey();
        setError(null);
        setUploadPhase('idle');
        setIsUploading(false);
        
        // Call onUpload to refresh the page/data
        onUpload();
        
        // Wait a bit to show success state, then close
        setTimeout(() => {
          onClose();
        }, 1000);
      } else if (abortReasonRef.current === 'user') {
        // They stopped on purpose. Keep their photos and caption, say nothing
        // alarming — a red banner for an intentional action reads as a fault.
        setUploadPhase('idle');
        setIsUploading(false);
      } else {
        // The thunk already translated the failure into words the creator can
        // act on — show those, never a generic "upload failed". A stall is our
        // own verdict, so we supply that copy directly.
        const reason =
          abortReasonRef.current === 'stalled'
            ? friendlyMessageForCode(ERR.NETWORK_TIMEOUT, UPLOAD_FALLBACK_MESSAGE)
            : (result.payload as ArchivePostError | undefined)?.message ?? UPLOAD_FALLBACK_MESSAGE;
        setError(reason);
        setUploadPhase('idle');
        setIsUploading(false);
      }
    } catch (err) {
      // Only unexpected client-side faults land here; dispatch itself resolves.
      console.error('Upload error:', err);
      setError(UPLOAD_FALLBACK_MESSAGE);
      toast.error(UPLOAD_FALLBACK_MESSAGE);
      setUploadPhase('idle');
      setIsUploading(false);
    } finally {
      clearUploadTimers();
      abortRef.current = null;
    }
  };

  /** Stop an upload the user no longer wants. Keeps the form intact. */
  const handleCancelUpload = () => {
    abortReasonRef.current = 'user';
    abortRef.current?.abort();
  };

  // Release object URLs when the modal goes away for good.
  //
  // Deliberately depends on nothing: with [previewUrls] the cleanup runs on
  // every change and would revoke URLs that are still on screen. The ref
  // keeps the unmount handler pointed at the current list.
  const previewUrlsRef = useRef<string[]>([]);
  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  /**
   * Pinned action bar. Rendered as a sibling of the scroll area (never inside
   * it) so it sits flush at the bottom with no gap and no dead space, at the
   * same height as the app's bottom nav.
   */
  const renderActionBar = (paddingClass: string) =>
    selectedFiles.length > 0 ? (
      <div
        className={`flex items-center gap-3 py-3 md:py-4 border-t border-gray-100 bg-white ${paddingClass}`}
      >
        <button
          type="button"
          onClick={isUploading ? handleCancelUpload : onClose}
          className="flex-1 h-12 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
        >
          {isUploading ? 'Stop' : 'Cancel'}
        </button>
        <button
          type="button"
          onClick={handleUpload}
          disabled={isUploading || selectedFiles.length === 0}
          className="flex-[1.7] h-12 px-4 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
        >
          {isUploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
              <span>{uploadPhase === 'processing' ? 'Saving...' : 'Uploading...'}</span>
            </>
          ) : (
            <span>Create Archive Post</span>
          )}
        </button>
      </div>
    ) : null;

  const uploadPercent =
    bytesTotal > 0 ? Math.min(100, Math.round((bytesSent / bytesTotal) * 100)) : 0;

  const modalContent = (
    <div>
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300 mb-4">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className={`flex-1 space-y-5 pb-4 ${selectedFiles.length === 0 ? 'flex items-center justify-center min-h-[400px]' : ''}`}>

        {/* Upload progress — one honest bar. Every photo travels in a single
            request, so per-file percentages would be invented. */}
        {isUploading && (
          <div className="space-y-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                {uploadPhase === 'processing' ? 'Almost done' : 'Uploading your photos'}
              </h3>
              {uploadPhase === 'uploading' && bytesTotal > 0 && (
                <span className="text-xs font-semibold text-[#e9b306] tabular-nums">
                  {uploadPercent}%
                </span>
              )}
            </div>

            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#e9b306] transition-all duration-300"
                style={{ width: `${uploadPhase === 'processing' ? 100 : uploadPercent}%` }}
              />
            </div>

            <p className="text-xs text-gray-600">
              {uploadPhase === 'processing'
                ? 'Saving your post. This only takes a moment.'
                : `${formatMB(bytesSent)} of ${formatMB(bytesTotal)} · ${selectedFiles.length} photo${selectedFiles.length > 1 ? 's' : ''}`}
            </p>
            <p className="text-xs text-gray-500">
              Keep this page open. On a slow connection this can take a few minutes.
            </p>
          </div>
        )}

          {/* Image Upload Area */}
          <div>
            {selectedFiles.length === 0 ? (
              <div>
                {/* Mobile: Show camera button */}
                {isMobile ? (
                  <div className="space-y-6 w-full max-w-sm mx-auto">
                    {/* Hero Section */}
                    <div className="text-center py-8">
                      <div className="mx-auto w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-50 rounded-3xl flex items-center justify-center mb-6">
                        <ImageIcon className="text-gray-400" size={32} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Share Your Work</h3>
                      <p className="text-sm text-gray-500 px-4 leading-relaxed">
                        Create an archive post to showcase your creative work and connect with the community
                      </p>
                    </div>
                    
                    {/* Features List */}
                    <div className="space-y-3 px-2">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                        <span>Upload up to 10 high-quality images</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-gray-500 rounded-full flex-shrink-0"></div>
                        <span>Add captions and tags to tell your story</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-gray-600 rounded-full flex-shrink-0"></div>
                        <span>Choose to publish or save as draft</span>
                      </div>
                    </div>
                    
                    {/* Upload Button */}
                    <button
                      onClick={() => setShowMobileCapture(true)}
                      className="w-full bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white py-5 rounded-2xl font-semibold transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg"
                    >
                      <ImageIcon size={22} />
                      <span>Add Photos</span>
                    </button>
                    
                    <div className="text-center space-y-2">
                      <p className="text-xs text-gray-500">
                        Take photos with camera or choose from your gallery
                      </p>
                      <p className="text-xs text-gray-400">
                        {ACCEPTED_FORMATS_LABEL} • up to 10MB each
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Desktop: Show drag & drop */
                  <div className="w-full max-w-lg mx-auto">
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
                        dragActive 
                          ? 'border-[#e9b306] bg-[#e9b306]/5 scale-[1.02]' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                      }`}
                    >
                      <div className="space-y-6">
                        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#e9b306]/10 to-[#e9b306]/5 rounded-2xl flex items-center justify-center">
                          <ImageIcon className="text-[#e9b306]" size={32} />
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                              Share Your Creative Work
                            </h3>
                            <p className="text-sm text-gray-600 mb-4 max-w-sm mx-auto leading-relaxed">
                              Create an archive post to showcase your work and connect with the community
                            </p>
                          </div>
                          
                          {/* Features List */}
                          <div className="space-y-2 text-left max-w-xs mx-auto">
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                              <span>Upload up to 10 high-quality images</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full flex-shrink-0"></div>
                              <span>Add captions and tags to tell your story</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              <div className="w-2 h-2 bg-gray-600 rounded-full flex-shrink-0"></div>
                              <span>Choose to publish or save as draft</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <p className="text-gray-700 font-medium">
                            Drop your images here
                          </p>
                          <p className="text-sm text-gray-500">or</p>
                          <label className="inline-block cursor-pointer">
                            <span className="inline-flex items-center gap-2 px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-all duration-200 hover:scale-105">
                              <Upload size={18} />
                              Browse Files
                            </span>
                            <input
                              type="file"
                              accept={ACCEPT_ATTRIBUTE}
                              multiple
                              onChange={handleFileInput}
                              className="hidden"
                            />
                          </label>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500">
                            {ACCEPTED_FORMATS_LABEL} • up to 10MB each
                          </p>
                          <p className="text-xs text-gray-400">
                            Drag and drop multiple files or click to browse
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">

                {/* Photo row — order is the post order, first photo is the cover */}
                <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
                  {previewUrls.map((image: string, index: number) => (
                    <div
                      key={index}
                      className={`relative group flex-shrink-0 w-[132px] aspect-square snap-start overflow-hidden rounded-xl bg-gray-100 border-2 transition-all duration-200 ${
                        index === coverIndex ? 'border-gray-900' : 'border-transparent'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setCoverIndex(index)}
                        disabled={isUploading}
                        aria-label={index === coverIndex ? 'Cover photo' : `Make photo ${index + 1} the cover`}
                        className="absolute inset-0 w-full h-full"
                      >
                        {/* Sits behind the image. Chrome and Firefox cannot
                            render HEIC, so without this an iPhone photo shows
                            as a blank tile and looks like a failed upload —
                            it uploads and converts perfectly well. */}
                        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center bg-gray-100">
                          <ImageIcon size={18} className="text-gray-400" />
                          <span className="text-[10px] leading-tight text-gray-500">
                            Ready to upload
                          </span>
                        </span>
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          className="relative w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      </button>

                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        disabled={isUploading}
                        className="absolute top-2 right-2 p-1.5 bg-gray-900/70 backdrop-blur-md text-white rounded-full hover:bg-gray-900 active:scale-95 transition-all duration-200"
                        title="Remove photo"
                      >
                        <X size={13} />
                      </button>

                      {/* Adjust — kept available without crowding the tile */}
                      <button
                        type="button"
                        onClick={() => setCropIndex(index)}
                        disabled={isUploading}
                        className={`absolute bottom-2 right-2 p-1.5 bg-white/95 backdrop-blur-md text-gray-700 rounded-full shadow transition-all duration-200 hover:bg-white active:scale-95 ${
                          isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                        title="Adjust photo"
                      >
                        <Crop size={13} />
                      </button>

                      {index === coverIndex && (
                        <span className="absolute bottom-2 left-2 px-2 py-1 bg-white text-gray-900 text-[10px] font-bold uppercase tracking-wide rounded-md shadow-sm">
                          Cover
                        </span>
                      )}
                    </div>
                  ))}

                  {/* Add More */}
                  {previewUrls.length < MAX_FILES && (
                    <label className="flex-shrink-0 w-[132px] aspect-square snap-start cursor-pointer">
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 group">
                        <Plus size={22} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                        <span className="text-xs font-medium text-gray-500 group-hover:text-gray-700 transition-colors">
                          Add More
                        </span>
                      </div>
                      <input
                        type="file"
                        accept={ACCEPT_ATTRIBUTE}
                        multiple
                        onChange={handleFileInput}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  {previewUrls.length} of {MAX_FILES} · tap a photo to make it the cover
                </p>
              </div>
            )}
          </div>

        {/* Caption Input with @ Mentions */}
        {selectedFiles.length > 0 && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Caption <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <MentionTextarea
              value={caption}
              onChange={(newCaption, mentionedUserIds) => {
                setCaption(newCaption);
                setMentionedUsers(mentionedUserIds);
              }}
              placeholder="Tell the story behind your work... Use @ to mention users"
              maxLength={MAX_CAPTION_LENGTH}
              disabled={isUploading}
              rows={4}
            />
            <div className="flex justify-end items-center mt-2">
              <p className="text-xs text-gray-400">
                {caption.replace(/@\[.*?\]\(.*?\)/g, (match) => {
                  const display = match.match(/@\[(.*?)\]/)?.[1] || '';
                  return `@${display}`;
                }).length}/{MAX_CAPTION_LENGTH}
              </p>
            </div>
          </div>
        )}

        {/* Discovery filters — occasion, tags and ready time */}
        {selectedFiles.length > 0 && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <PostFiltersSection value={filters} onChange={setFilters} disabled={isUploading} />
          </div>
        )}

        {/* Status Selection */}
        {selectedFiles.length > 0 && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Visibility
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="relative">
                <input
                  type="radio"
                  value="published"
                  checked={status === 'published'}
                  onChange={(e) => setStatus(e.target.value as 'published')}
                  disabled={isUploading}
                  className="sr-only peer"
                />
                <div className="p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all duration-200 peer-checked:border-gray-900 peer-checked:bg-gray-50 hover:border-gray-300 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
                  <div className="font-semibold text-gray-900 mb-1">Published</div>
                  <div className="text-xs text-gray-500">Visible to everyone</div>
                </div>
              </label>
              <label className="relative">
                <input
                  type="radio"
                  value="draft"
                  checked={status === 'draft'}
                  onChange={(e) => setStatus(e.target.value as 'draft')}
                  disabled={isUploading}
                  className="sr-only peer"
                />
                <div className="p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all duration-200 peer-checked:border-gray-900 peer-checked:bg-gray-50 hover:border-gray-300 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
                  <div className="font-semibold text-gray-900 mb-1">Draft</div>
                  <div className="text-xs text-gray-500">Only visible to you</div>
                </div>
              </label>
            </div>
          </div>
        )}

      </div>

    </div>
  );

  return (
    <>
      {/* Mobile Image Capture */}
      {isMobile && showMobileCapture && (
        <MobileImageCapture
          onCapture={(files) => {
            handleFiles(files);
            setShowMobileCapture(false);
          }}
          onClose={() => setShowMobileCapture(false)}
        />
      )}

      {/* Image Cropper */}
      {cropIndex !== null && (
        <ModernImageCropper
          image={previewUrls[cropIndex]}
          onCropComplete={(blob: Blob) => handleCropComplete(cropIndex, blob)}
          onCancel={() => setCropIndex(null)}
        />
      )}

      {/* Desktop: Traditional Modal */}
      <div className={`hidden sm:block fixed inset-0 z-[60] ${isOpen ? '' : 'pointer-events-none'}`}>
        <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
        <div className={`fixed inset-0 flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="bg-white rounded-3xl max-w-xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 p-4 md:p-6 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 font-headline">Create Archive Post</h2>
                <p className="text-sm text-gray-500 mt-1">Share your creative work</p>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 hover:rotate-90"
                disabled={isUploading}
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 md:p-6 space-y-5 overflow-y-auto flex-1 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              {modalContent}
            </div>

            {renderActionBar('px-4 md:px-6 rounded-b-3xl')}
          </div>
        </div>
      </div>

      {/* Mobile: MobileModal */}
      <MobileModal
        isOpen={isOpen && isMobile}
        onClose={onClose}
        contentClassName="text-left p-0"
        footer={renderActionBar('px-6')}
      >
        {modalContent}
      </MobileModal>
    </>
  );
};

export default ArchiveUploadModal;
